import { routeTree, flattenRoutes, type RouteNode } from "./config";
// Where Stripe Checkout returns to; forwards into the Billing tab.
import { BillingReturn } from "@/pages/org-admin/BillingReturn";

// Component imports
import { Dashboard } from "@/pages/dashboard/Dashboard";
import { Profile } from "@/pages/profile";
import { AIAssistant } from "@/pages/ai-assistant";
import { ModerniseWithAI } from "@/pages/modernise-with-ai";
import { SlackLink } from "@/pages/slack-link";

// Auth Pages
import { Login } from "@/pages/auth/Login";
import { SalesforceCallback } from "@/pages/auth/SalesforceCallback";

// Metadata Pages
import { MetadataV1 } from "@/pages/metadata_v1";

// SOQL Pages
import { QueryBuilder } from "@/pages/soql/QueryBuilder";
import { DataExport } from "@/pages/soql/DataExport";
import { DataImport } from "@/pages/soql/DataImport";
import { FieldInspector } from "@/pages/soql/FieldInspector";
import { ObjectInspector } from "@/pages/soql/ObjectInspector";
import { TextToSOQL } from "@/pages/text-to-soql";

// Deployment Pages
import { Deploy } from "@/pages/deployment/Deploy";
import { Retrieve } from "@/pages/deployment/Retrieve";
import { PackageBuilder } from "@/pages/deployment/PackageBuilder";
import { FileDetails } from "@/pages/deployment/FileDetails";

// Security Pages
import { SecurityScanner } from "@/pages/security-scanner";

// Testing (chat-driven, cloud-native)
import { TestingPage } from "@/pages/testing";
import { TestingAnalytics } from "@/pages/testing/TestingAnalytics";
import { DebuggingPage } from "@/pages/debugging/DebuggingPage";

// Agent Pages
import {
  AgentCreateRoute,
  AgentRunRoute,
  AgentWorkspaceRoute,
  AgentsRoute,
  Org360Route,
  ReportsRoute,
  RunsRoute,
  SalesforceReleasesRoute,
  SchedulingRoute,
  SkillsConnectorsRoute,
  GitRoute,
  StudioOrgAdminRoute,
  StudioConnectionRoute,
  StudioDeploymentRoute,
  StudioDocsRoute,
  StudioGitRoute,
  StudioMetadataRoute,
  StudioSecurityRoute,
  TemplateRunRoute,
  TemplateWorkspaceRoute,
  TemplatesRoute,
} from "@/pages/agent/agent-studio";
import { MigrationCreate } from "@/pages/migrations/MigrationCreate";
import { EnginePlans } from "@/pages/migrations/EnginePlans";
import { EnginePlanDetail } from "@/pages/migrations/EnginePlanDetail";
import { PortfolioPass } from "@/pages/migrations/PortfolioPass";
import { TrainingData } from "@/pages/agent/training-data";
import { AgentTesting } from "@/pages/agent/agent-testing";
import { AgentDeployment } from "@/pages/agent/agent-deployment";
import { KnowledgeBase } from "@/pages/agent/knowledge-base";
import { VoiceConfig } from "@/pages/agent/voice-config";

import { DevAgent } from "@/pages/dev-agent";
import { MultiAgentRoute } from "@/pages/multi-agent/MultiAgentRoute";
import { Weave2Route } from "@/pages/weave2/Weave2Route";
import {
  MultiAgentLayoutClassic,
  MultiAgentLayoutCommand,
  MultiAgentLayoutFocus,
  MultiAgentLayoutSplit,
  MultiAgentLayoutZen,
} from "@/pages/multi-agent/layouts";

// Organization Pages
import { ConnectionManager } from "@/pages/org/ConnectionManager";
import { Authentication } from "@/pages/org/Authentication";

// Support Pages
import { HelpCenter } from "@/pages/support/HelpCenter";
import { TeamCollaboration } from "@/pages/team-collaboration";
import { HelpSupport } from "@/pages/help-support";
import { Documentation } from "@/pages/documentation";
import { ReleaseNotes } from "@/pages/release-notes";
import { UserGuide } from "@/pages/user-guide";
import { Upgrade } from "@/pages/upgrade";
import { VersionControl } from "@/pages/version-control";

// Legacy Pages
import { HomePage } from "@/pages/home";
import { AboutPage } from "@/pages/about";
import { NotFound } from "@/pages/not-found";

// Component mapping
const componentMap: Record<string, React.ComponentType> = {
  // Main
  Dashboard,
  Profile,
  AIAssistant,
  ModerniseWithAI,
  SlackLink,

  // Auth
  Login,
  SalesforceCallback,

  // Metadata
  MetadataV1,

  // SOQL
  QueryBuilder,
  DataExport,
  DataImport,
  FieldInspector,
  ObjectInspector,
  TextToSOQL,

  // Deployment
  Deploy,
  Retrieve,
  PackageBuilder,
  FileDetails,

  // Security
  SecurityScanner,

  // Testing
  TestingRoute: TestingPage,
  TestingAnalyticsRoute: TestingAnalytics,
  DebuggingRoute: DebuggingPage,

  // Playbooks
  PlaybookCreateRoute: MigrationCreate,

  // Migration engine (self-gating: renders an explanation where the API is absent)
  MigrationEngineRoute: EnginePlans,
  MigrationEnginePlanRoute: EnginePlanDetail,
  MigrationPortfolioRoute: PortfolioPass,

  // Agent
  GitRoute,
  TemplatesRoute,
  SkillsConnectorsRoute,
  TemplateWorkspaceRoute,
  TemplateRunRoute,
  AgentWorkspaceRoute,
  AgentCreateRoute,
  AgentRunRoute,
  RunsRoute,
  ReportsRoute,
  AgentsRoute,
  SalesforceReleasesRoute,
  SchedulingRoute,
  Org360Route,
  MultiAgentRoute,
  Weave2Route,
  MultiAgentLayoutClassic,
  MultiAgentLayoutFocus,
  MultiAgentLayoutCommand,
  MultiAgentLayoutSplit,
  MultiAgentLayoutZen,
  StudioMetadataRoute,
  StudioDeploymentRoute,
  StudioGitRoute,
  StudioSecurityRoute,
  StudioDocsRoute,
  StudioOrgAdminRoute,
  BillingReturn,
  StudioConnectionRoute,
  TrainingData,
  AgentTesting,
  AgentDeployment,
  KnowledgeBase,
  VoiceConfig,

  // Chat
  DevAgent,

  // Organization
  ConnectionManager,
  Authentication,

  // Support
  HelpCenter,
  TeamCollaboration,
  HelpSupport,
  Documentation,
  ReleaseNotes,
  UserGuide,
  Upgrade,
  VersionControl,

  // Legacy/Dev
  HomePage,
  AboutPage,
  NotFound,
};

// Route configuration interface
export interface RouteConfig {
  path: string;
  Component: React.ComponentType;
  isRedirect?: boolean;
  redirectTo?: string;
  metadata?: RouteNode["meta"];
}

// Generate route configurations from the tree structure
export function generateRouteConfigs(): RouteConfig[] {
  const allRoutes = flattenRoutes(routeTree);
  const routes: RouteConfig[] = [];

  allRoutes.forEach((route) => {
    // Skip routes without components (they're just groupings)
    if (!route.component && !route.meta?.redirect) {
      return;
    }

    // Handle redirects for legacy routes
    if (route.meta?.redirect) {
      routes.push({
        path: route.path,
        Component: () => null, // Will be handled as redirect in App.tsx
        isRedirect: true,
        redirectTo: route.meta.redirect,
        metadata: route.meta,
      });
      return;
    }

    // Get the component
    const Component = componentMap[route.component!];
    if (!Component) {
      console.warn(
        `Component ${route.component} not found for route ${route.path}`
      );
      return;
    }

    routes.push({
      path: route.path,
      Component,
      metadata: route.meta,
    });
  });

  return routes;
}

// Get route metadata by path
export function getRouteMetadata(path: string): RouteNode["meta"] | undefined {
  const allRoutes = flattenRoutes(routeTree);
  const route = allRoutes.find((r) => r.path === path);
  return route?.meta;
}

// Check if route is protected
export function isRouteProtected(path: string): boolean {
  const metadata = getRouteMetadata(path);
  return metadata?.protected !== false;
}

// Check if route uses auth layout
export function usesAuthLayout(path: string): boolean {
  const metadata = getRouteMetadata(path);
  return metadata?.layout === "auth";
}

// Get routes by group for navigation
export function getRoutesByGroup(group: string): RouteNode[] {
  const allRoutes = flattenRoutes(routeTree);
  return allRoutes.filter(
    (route) =>
      route.meta?.group === group && !route.meta?.legacy && route.component
  );
}

// Export the route tree for external use
export { routeTree } from "./config";
